import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  DismissibleTag,
  FilterableMultiSelect,
  MenuButton,
  MenuItem,
  MenuItemRadioGroup,
  OverflowMenu,
  OverflowMenuItem,
  Tag,
} from '@carbon/react';
import { Settings } from '@carbon/react/icons';
import type { ColumnPinningState, SortingState } from '@tanstack/react-table';
import moment from 'moment';
import {
  DataTable,
  formatCellValue,
  type DataTableColumnDef,
  type DataTableColumnMeta,
  type TableState,
} from 'components/data-table';
import { formatNumber } from 'utils/number';
import { linkCellRenderer, MASTER_DATA_PAGE_SIZES } from './utils';
import { CashflowPeriodEditor } from './cashflow-period-editor';
import { ASSET_FIELD } from './custom-chart/spec-chart-render';
import type {
  CashflowPeriod,
  CashflowPeriodSummary,
  RollupBin,
  RollupBinOption,
  RollupColumn,
  RollupDimensionOption,
  RollupFormat,
  RollupRow,
  RollupSort,
  RollupTotals,
} from './types';
import styles from './value-rollup-table.module.scss';

/** One Group-by dropdown option — Carbon needs an `id`; the rollup key IS the id. */
interface GroupByItem {
  id: string;
  label: string;
}

/** Whole-dollar currency meta, reused for the totals / period-summary lines. */
const CURRENCY_META: DataTableColumnMeta = {
  type: 'currency',
  format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
};

/** Whole-number meta for the period-summary oil/gas volume values (no unit rendered). */
const VOLUME_META: DataTableColumnMeta = { type: 'integer' };

/** Row id + marker for the synthetic grand-total row appended as the last row. */
const TOTAL_ROW_ID = '__rollup_total__';
const TOTAL_ROW_MARKER = '__isRollupTotal__';

/** BE column key for the numeric-bin leading pseudo-dimension (pinned-left, non-sortable). */
const NUMERIC_BIN_COLUMN_KEY = 'numericBin';

/** Display word per bin category — single-sourced for the trigger label + the active-bin chip. */
const BIN_CATEGORY_LABELS: Record<RollupBin['category'], string> = {
  numeric: 'Numeric',
  temporal: 'Temporal',
};

/**
 * Column width (px) per format. Numeric columns (currency / decimal / integer) share a
 * wide size — values reach millions/billions and, with % of Package on, a trailing %
 * pill — so e.g. `$134,120,660  24.3%` stays on one line.
 *
 * @param {RollupFormat} format - The BE-provided column format.
 * @return {number} The column width in px.
 */
function sizeForFormat(format: RollupFormat): number {
  switch (format) {
    case 'currency':
    case 'decimal':
    case 'integer':
      return 210;
    case 'date':
      return 140;
    case 'text':
    default:
      return 160;
  }
}

/**
 * Maps a rollup column `format` to the DataTable cell `meta` (type + precision).
 * Dimension columns are `text` and render verbatim (the FE is a pure renderer — no
 * re-casing); metric columns format as integer counts (0dp), decimals FIXED at 2dp
 * (min = max, so values always show exactly two places), or whole-dollar currency
 * (0dp — integer), or render a date.
 *
 * @param {RollupFormat} format - The BE-provided column format.
 * @return {DataTableColumnMeta} The DataTable cell meta (type + precision).
 */
export function metaForFormat(format: RollupFormat): DataTableColumnMeta {
  switch (format) {
    case 'integer':
      return { type: 'integer' };
    case 'decimal':
      return {
        type: 'decimal',
        format: { minDecimalPlaces: 2, maxDecimalPlaces: 2 },
      };
    case 'currency':
      return CURRENCY_META;
    case 'date':
      return { type: 'date' };
    // Percent cells are formatted by the caller (`formatPct`); this explicit case keeps the
    // switch exhaustive over RollupFormat so a future consumer can't silently fall to text.
    case 'percent':
    case 'text':
    default:
      return { type: 'text' };
  }
}

/**
 * Formats a `pctOfPackage` fraction (0–1) as a 1dp percentage (0.313 → "31.3%").
 *
 * @param {number} fraction - The BE-computed fraction (0–1).
 * @return {string} The percent string.
 */
export function formatPct(fraction: number): string {
  return `${formatNumber({
    value: fraction * 100,
    minPrecision: 1,
    maxPrecision: 1,
    includeCommas: true,
  })}%`;
}

/**
 * Adapts the domain `RollupSort` to DataTable's `SortingState` (single-column).
 *
 * @param {RollupSort | null} sort - The domain sort, or null (unsorted).
 * @return {SortingState} The DataTable sorting state (0 or 1 entry).
 */
function toSortingState(sort: RollupSort | null): SortingState {
  return sort ? [{ id: sort.field, desc: sort.dir === 'desc' }] : [];
}

/**
 * Adapts DataTable's `SortingState` back to `RollupSort` — single-column (primary
 * entry only), empty ⇒ null.
 *
 * @param {SortingState} sorting - The DataTable sorting state.
 * @return {RollupSort | null} The domain sort, or null when unsorted.
 */
function fromSortingState(sorting: SortingState): RollupSort | null {
  const primary = sorting[0];
  return primary
    ? { field: primary.id, dir: primary.desc ? 'desc' : 'asc' }
    : null;
}

/**
 * The applied period as a chip label — "Apr 2026 – Dec 2030".
 *
 * @param {CashflowPeriod} period - The applied window.
 * @return {string} The month-range label.
 */
function periodChipLabel(period: CashflowPeriod): string {
  const from = moment(period.start).format('MMM YYYY');
  const to = moment(period.end).format('MMM YYYY');
  return `${from} – ${to}`;
}

/**
 * The stable row id for a rollup row — its pivot GRAIN: the values of the leading label
 * columns (`idKeys`: the numeric-bin column when present, then the group-by dimensions).
 * Only keys the row actually carries contribute, so an id can't collapse to `"[null]"`
 * during a `keepPreviousData` transition; and because the bin label is part of the grain,
 * two rows in different bins that share dimension values get DISTINCT ids.
 *
 * @param {RollupRow} row - The row to identify.
 * @param {string[]} idKeys - The ordered grain keys (bin column first, then dimensions).
 * @return {string} A stable id (JSON of the present grain values).
 */
export function rollupRowId(row: RollupRow, idKeys: string[]): string {
  const present = idKeys.filter((key) => key in row);
  return JSON.stringify(present.map((key) => row[key]));
}

export interface ValueRollupTableProps {
  /**
   * The server result: the BE-driven columns, the page's rows, the grand `totals`
   * (no `pctOfPackage`), the applied cashflow `periodSummary` (or null), and the
   * offset `totalCount`.
   */
  data: {
    columns: RollupColumn[];
    rows: RollupRow[];
    totals: RollupTotals;
    periodSummary: CashflowPeriodSummary | null;
    totalCount: number;
  };
  /**
   * The Group-by control (live, multi-select): the config dimension `options`, the
   * currently `selected` keys, and `onChange`. `selected` also identifies which
   * columns are dimensions and forms the row id. Empty ⇒ the no-dimension empty state.
   */
  groupBy: {
    options: RollupDimensionOption[];
    selected: string[];
    onChange: (keys: string[]) => void;
  };
  /** Controlled single-column server sort (from `rollupSortAtom`). `null` = unsorted. */
  sort: {
    value: RollupSort | null;
    onChange: (next: RollupSort | null) => void;
  };
  /** Controlled global search — `value` is the IMMEDIATE text; `onChange` fires per keystroke. */
  search: {
    value: string;
    onChange: (query: string) => void;
  };
  /**
   * % of Package badges — `enabled` shows/hides them (no refetch; the fractions ride
   * every row); `onToggle` flips the flag.
   */
  percentOfPackage: {
    enabled: boolean;
    onToggle: (on: boolean) => void;
  };
  /**
   * The per-card cashflow period. `value` is the applied window (or null); `onApply` /
   * `onRemove` are driven from the gear's editor.
   */
  period: {
    value: CashflowPeriod | null;
    onApply: (period: CashflowPeriod) => void;
    onRemove: () => void;
  };
  /**
   * The Bin by control — the catalog `options` (numeric + temporal presets), the single
   * active `value` (or null for "None"), and `onChange`. A numeric bin also adds the
   * BE `numericBin` leading column; the two categories are mutually exclusive (one value).
   */
  bin: {
    options: { numeric: RollupBinOption[]; temporal: RollupBinOption[] };
    value: RollupBin | null;
    onChange: (next: RollupBin | null) => void;
  };
  /** Umbrella loading (own fetch OR the calc running) — dims the table in place. */
  isLoading: boolean;
  /** Controlled offset pagination — the section is the single page owner. */
  pagination: {
    page: number;
    pageSize: number;
    onChange: (page: number, pageSize: number) => void;
  };
  /**
   * The "(N of M Subtracts)" after an Asset that names a Tract the package holds in part
   * (`usePartialTracts().labelForName`); null for any other.
   */
  labelForName: (name: string) => string | null;
}

/**
 * Presentational Value Rollup table — a `DataTable` (offset / paginated server-driven
 * mode) with the live Group-by multi-select (`leftToolbarActions`), the gear
 * (`rightToolbarActions`: % of Package toggle + Cashflow Period editor), the removable
 * chip row (`betweenToolbarAndContent`), and a card-header summary line (the grand
 * totals + the applied-period slice-vs-total). Pure: data + callbacks in, no atoms /
 * context / fetching (the section owns those).
 *
 * Dynamic BE-driven columns: the column SET changes as the Group-by dimensions change,
 * and `DataTable` reconciles its `columnOrder` against the live `columns` prop with **no
 * remount** (a new dimension lands at its BE position, a removed one drops) — which is why
 * the toolbar (the Group-by menu, its focus/filter-text/scroll) survives a regroup. Metrics
 * are drag-reorderable (`columnReordering`); dimension columns are pinned-left as fixed row
 * labels (controlled `columnPinning`, tracked from `selected`) and, being pinned, aren't
 * draggable. Sort + search are CONTROLLED from atoms (FR#18). % badges ride every metric
 * cell (shown when the toggle is on AND the metric is in the row's `pctOfPackage`); no refetch.
 *
 * @param {ValueRollupTableProps} props - The server result + all card controls.
 * @return {JSX.Element} The summary line over the rollup DataTable.
 */
export function ValueRollupTable({
  data,
  groupBy,
  sort,
  search,
  percentOfPackage,
  period,
  bin,
  isLoading,
  pagination,
  labelForName,
}: ValueRollupTableProps) {
  const { selected, options, onChange: onGroupByChange } = groupBy;
  const { enabled: showPercent, onToggle: onTogglePercent } = percentOfPackage;
  const { options: binOptions, value: activeBin, onChange: onBinChange } = bin;

  // Local UI state: whether the gear's Cashflow Period editor is open (visibility
  // only — the applied value lives in the section's atom).
  const [isPeriodEditorOpen, setIsPeriodEditorOpen] = useState(false);

  // Column pinning. Dimension columns start pinned-left and STAY pinned (they're the row
  // labels); the user may additionally pin/unpin any metric. We own the state so the current
  // dimensions are always forced into `left` (tracking regroups via `selected`), with the
  // user's own pins layered on top. `userPinning` captures what the user explicitly set;
  // trying to unpin a dimension is a no-op because the derive re-forces it.
  const [userPinning, setUserPinning] = useState<ColumnPinningState>({
    left: [],
    right: [],
  });
  // Does THIS response carry the numeric-bin leading column? Read from the BE-driven columns
  // (not the bin prop) so it stays consistent through a `keepPreviousData` transition.
  const hasNumericBinColumn = useMemo(
    () => data.columns.some((column) => column.key === NUMERIC_BIN_COLUMN_KEY),
    [data.columns]
  );
  // The leading label columns of this response, IN ORDER: the numeric-bin column (when the BE
  // returned it) then the grouped dimension columns. Read from `data.columns` (which moves
  // ATOMICALLY with `data.rows`), so it's transition-safe — ONE source for both the row-id
  // grain and the total-row "Total" placement, keeping them aligned to the rendered columns
  // rather than the `selected` atom (which flips a render early under keepPreviousData).
  const dimensionKeySet = useMemo(
    () => new Set(options.map((option) => option.key)),
    [options]
  );
  const leadingLabelKeys = useMemo(
    () =>
      data.columns
        .map((column) => column.key)
        .filter(
          (key) => key === NUMERIC_BIN_COLUMN_KEY || dimensionKeySet.has(key)
        ),
    [data.columns, dimensionKeySet]
  );
  const columnPinning = useMemo<ColumnPinningState>(() => {
    // The numeric-bin leading column pins left as the primary row label (before dimensions).
    const binLeft = hasNumericBinColumn ? [NUMERIC_BIN_COLUMN_KEY] : [];
    const userLeft = (userPinning.left ?? []).filter(
      (key) => !selected.includes(key) && key !== NUMERIC_BIN_COLUMN_KEY
    );
    return {
      left: [...binLeft, ...selected, ...userLeft],
      right: userPinning.right ?? [],
    };
  }, [hasNumericBinColumn, selected, userPinning]);

  // Dynamic, BE-driven columns. Each cell formats via DataTable's own `formatCellValue`
  // (so a custom cell doesn't lose type formatting) and appends a % badge when the
  // toggle is on AND the metric key is in the row's `pctOfPackage`. Rebuilt on a
  // column-set OR %-toggle change.
  const columns = useMemo<DataTableColumnDef<RollupRow>[]>(
    () =>
      data.columns.map((column) => {
        const meta = metaForFormat(column.format);
        return {
          id: column.key,
          accessorKey: column.key,
          header: column.label,
          size: sizeForFormat(column.format),
          // Rows are always bin-grouped first server-side, so the numeric-bin leading
          // column isn't user-sortable (and the BE maps no sort field for it).
          enableSorting:
            column.key === NUMERIC_BIN_COLUMN_KEY ? false : undefined,
          // `wrap` lets long cell content wrap (row height grows to fit) instead of
          // truncating — the `asset-view` pattern. `meta` (without wrap) still drives
          // the cell's `formatCellValue`.
          meta: { ...meta, wrap: true },
          cell: (info) => {
            const value = info.getValue();
            // The grand-total row: bold; the empty dimension cells render blank (not
            // formatCellValue's em-dash); no % badge.
            if (info.row.id === TOTAL_ROW_ID) {
              const text =
                value === undefined || value === ''
                  ? ''
                  : formatCellValue(value, meta);
              return <span className={styles.totalCell}>{text}</span>;
            }
            // Linkable dimension cell (slot / devArea / asset): the BE supplies the
            // detail-page href in the row's `hrefs` map — render it as a new-tab link. An
            // Asset (always linked) that names a Tract held in part says how much of it the
            // package holds.
            const href = (info.row.original as RollupRow).hrefs?.[column.key];
            if (href) {
              const link = linkCellRenderer(String(value ?? ''), href);
              const held =
                column.key === ASSET_FIELD && typeof value === 'string'
                  ? labelForName(value)
                  : null;
              return held ? (
                <>
                  {link} {held}
                </>
              ) : (
                link
              );
            }
            const formatted = formatCellValue(value, meta);
            const pct = (info.row.original as RollupRow).pctOfPackage?.[
              column.key
            ];
            if (!showPercent || pct === undefined) {
              return formatted;
            }
            return (
              <>
                {formatted}
                <span className={styles.pctBadge}>{formatPct(pct)}</span>
              </>
            );
          },
        };
      }),
    [data.columns, showPercent, labelForName]
  );

  // Row identity = the pivot GRAIN = the leading label columns' values (bin, then dimensions).
  // `rollupRowId` reads only keys the row carries, so an id can't collapse to `"[null]"` during
  // a regroup; and because the numeric-bin label is IN the grain, two rows sharing dimension
  // values in different bins get DISTINCT ids — otherwise they'd collide onto one id, React
  // would reuse the wrong <tr>/<tbody>, and the wrap-mode virtualizer's per-row measure cache
  // would leave stale cells under the fresh header (a misaligned table after a regroup). The
  // grand-total row (marker-tagged) keeps its fixed id.
  const getRowId = useCallback(
    (row: RollupRow) => {
      if (row[TOTAL_ROW_MARKER] === 1) {
        return TOTAL_ROW_ID;
      }
      return rollupRowId(row, leadingLabelKeys);
    },
    [leadingLabelKeys]
  );

  // Append the grand-total row (from `data.totals`) as the LAST row so it aligns to the
  // columns. Server-driven mode renders rows as-is (no client sort), so it stays pinned at
  // the bottom on every page. "Total" goes in the FIRST label column (the numeric-bin column
  // when present, else the first dimension); the other label columns are blank. Only when
  // there's data (an empty result shows no total row).
  const rows = useMemo(() => {
    if (data.rows.length === 0) {
      return data.rows;
    }
    const totalRow: RollupRow = { ...data.totals, [TOTAL_ROW_MARKER]: 1 };
    leadingLabelKeys.forEach((key, index) => {
      totalRow[key] = index === 0 ? 'Total' : '';
    });
    return [...data.rows, totalRow];
  }, [data.rows, data.totals, leadingLabelKeys]);

  const { onChange: onPaginationChange } = pagination;
  const handlePaginationChange = useCallback(
    (state: TableState) => onPaginationChange(state.page, state.pageSize),
    [onPaginationChange]
  );

  // Controlled sort (single-column; header cycles asc → desc → none). ⚠️ BE gap
  // (INS-10097): sorting the slot-only date columns is a server no-op — flag to Piyush.
  const { value: sortValue, onChange: onSortChange } = sort;
  const controlledSorting = useMemo(
    () => toSortingState(sortValue),
    [sortValue]
  );
  const handleSortChange = useCallback(
    (state: TableState) => onSortChange(fromSortingState(state.sorting)),
    [onSortChange]
  );

  // Controlled global search — the input reflects the IMMEDIATE value; the section
  // debounces the query write (server-side ILIKE, fixed in 57aff0b).
  const { value: searchValue, onChange: onSearchChange } = search;
  const handleGlobalSearchChange = useCallback(
    (state: TableState) => onSearchChange(state.globalFilter),
    [onSearchChange]
  );

  // Group-by dropdown items — Carbon needs an `id`; the rollup dimension key is the id.
  const groupByItems = useMemo<GroupByItem[]>(
    () => options.map((option) => ({ id: option.key, label: option.label })),
    [options]
  );
  const selectedItems = useMemo(
    () => groupByItems.filter((item) => selected.includes(item.id)),
    [groupByItems, selected]
  );
  const handleGroupBySelection = useCallback(
    ({ selectedItems: picked }: { selectedItems: GroupByItem[] }) =>
      onGroupByChange(picked.map((item) => item.id)),
    [onGroupByChange]
  );

  const groupByControl = (
    <FilterableMultiSelect<GroupByItem>
      id="value-rollup-group-by"
      className={styles.groupBy}
      aria-label="Group by dimensions"
      placeholder="Group by dimensions…"
      size="lg"
      items={groupByItems}
      itemToString={(item) => item?.label ?? ''}
      selectedItems={selectedItems}
      onChange={handleGroupBySelection}
    />
  );

  // ── Bin by (single MenuButton). The atom holds ONE value, so numeric and temporal are
  // mutually exclusive by construction — picking a preset replaces any prior one, "None"
  // clears. A numeric preset also adds the BE `numericBin` leading column (pinned above);
  // a temporal preset drives the separate summary table (rendered by the card). ──
  const activeBinLabel = useMemo(() => {
    if (!activeBin) {
      return null;
    }
    const presets =
      activeBin.category === 'numeric'
        ? binOptions.numeric
        : binOptions.temporal;
    return (
      presets.find((preset) => preset.key === activeBin.presetKey)?.label ??
      null
    );
  }, [activeBin, binOptions]);
  // Keep the trigger label SHORT + bounded so a long preset name can't grow the MenuButton
  // and squeeze the toolbar's flex-grow search field. The exact active preset shows in the
  // chip below — mirroring Group-by, whose trigger keeps a stable label while the selections
  // ride as chips (not spelled out in the trigger).
  const binButtonLabel = activeBin
    ? `${BIN_CATEGORY_LABELS[activeBin.category]} Bin`
    : 'Bin by';
  // Carbon's MenuItemRadioGroup selects by the item STRING (its visible label), so map the
  // picked label back to its preset key. Labels are unique per category, so no collision.
  const selectBinByLabel = useCallback(
    (category: 'numeric' | 'temporal', label: string) => {
      const presets =
        category === 'numeric' ? binOptions.numeric : binOptions.temporal;
      const preset = presets.find((entry) => entry.label === label);
      if (preset) {
        onBinChange({ category, presetKey: preset.key });
      }
    },
    [binOptions, onBinChange]
  );
  const binByControl = (
    <MenuButton
      label={binButtonLabel}
      kind="ghost"
      size="lg"
      menuAlignment="bottom-end"
    >
      {/* Disabled items render the VISIBLE section headers; each radio group also carries a
          non-empty label — Carbon exposes it as the group's aria-label (not visible text, so
          it doesn't duplicate the headers) which screen readers announce. Three radio groups,
          one shared selection: only the group matching the active bin's category shows a
          checked item (the rest resolve to ''). */}
      <MenuItemRadioGroup
        label="Bin selection"
        items={['None']}
        selectedItem={activeBin === null ? 'None' : ''}
        onChange={() => onBinChange(null)}
      />
      <MenuItem label="Numeric Bins" disabled />
      <MenuItemRadioGroup
        label="Numeric bins"
        items={binOptions.numeric.map((preset) => preset.label)}
        selectedItem={
          activeBin?.category === 'numeric' ? (activeBinLabel ?? '') : ''
        }
        onChange={(value: unknown) =>
          selectBinByLabel('numeric', String(value))
        }
      />
      <MenuItem label="Temporal Bins" disabled />
      <MenuItemRadioGroup
        label="Temporal bins"
        items={binOptions.temporal.map((preset) => preset.label)}
        selectedItem={
          activeBin?.category === 'temporal' ? (activeBinLabel ?? '') : ''
        }
        onChange={(value: unknown) =>
          selectBinByLabel('temporal', String(value))
        }
      />
    </MenuButton>
  );

  // The gear (right toolbar): % of Package toggle + Add/Edit Cashflow Period. The
  // editor itself renders in `betweenToolbarAndContent` (a menu item can't host it).
  const percentItemText = showPercent
    ? 'Hide % of Package'
    : 'Show % of Package';
  const periodItemText = period.value
    ? 'Edit Cashflow Period'
    : 'Add Cashflow Period';
  const gear = (
    <OverflowMenu
      aria-label="Rollup options"
      renderIcon={Settings}
      size="lg"
      flipped
    >
      <OverflowMenuItem
        className={styles.gearItem}
        itemText={percentItemText}
        onClick={() => onTogglePercent(!showPercent)}
      />
      <OverflowMenuItem
        className={styles.gearItem}
        itemText={periodItemText}
        onClick={() => setIsPeriodEditorOpen((open) => !open)}
      />
    </OverflowMenu>
  );

  // Between the toolbar (header) and the body rows, stacked top-to-bottom: the cashflow
  // slice-vs-total summary bar, then the Cashflow Period editor when open, then the
  // removable chip row. All pinned above the scrolling body; the chips show only once
  // dims are chosen. The BE ALWAYS returns `cashflowPeriodSummary` (the degenerate
  // full-window case — windowMonths === totalMonths, every `*Pct` = 1.0 — when no period
  // is applied), so the bar renders whenever the data is present, not only after Apply.
  // The `*Pct` values reflect the applied window; there is no live pre-Apply preview
  // (P6 has no BE preview endpoint), so editing the draft doesn't change the bar until
  // Apply.
  const periodSummary = data.periodSummary;
  const betweenToolbarAndContent = (
    <>
      {periodSummary && (
        <div className={styles.summary}>
          <span className={styles.summaryItem}>
            <strong>
              {formatNumber({
                value: periodSummary.windowMonths,
                minPrecision: 0,
                maxPrecision: 0,
                includeCommas: true,
              })}
            </strong>{' '}
            of{' '}
            {formatNumber({
              value: periodSummary.totalMonths,
              minPrecision: 0,
              maxPrecision: 0,
              includeCommas: true,
            })}{' '}
            months
            <Tag size="sm" type="gray">
              {formatPct(periodSummary.monthsPct)}
            </Tag>
          </span>
          <span className={styles.summaryItem}>
            Net CF:{' '}
            <strong>
              {formatCellValue(periodSummary.windowNetCashflow, CURRENCY_META)}
            </strong>
            <Tag size="sm" type="gray">
              {formatPct(periodSummary.netCashflowPct)}
            </Tag>
          </span>
          <span className={styles.summaryItem}>
            Revenue:{' '}
            <strong>
              {formatCellValue(periodSummary.windowRevenue, CURRENCY_META)}
            </strong>
            <Tag size="sm" type="gray">
              {formatPct(periodSummary.revenuePct)}
            </Tag>
          </span>
          <span className={styles.summaryItem}>
            Oil:{' '}
            <strong>
              {formatCellValue(periodSummary.windowNetOil, VOLUME_META)}
            </strong>
            <Tag size="sm" type="gray">
              {formatPct(periodSummary.netOilPct)}
            </Tag>
          </span>
          <span className={styles.summaryItem}>
            Gas:{' '}
            <strong>
              {formatCellValue(periodSummary.windowNetGas, VOLUME_META)}
            </strong>
            <Tag size="sm" type="gray">
              {formatPct(periodSummary.netGasPct)}
            </Tag>
          </span>
        </div>
      )}
      {isPeriodEditorOpen && (
        // Apply / Remove act in place and KEEP the editor open (Remove is right there
        // to undo, and Apply re-seeds the draft to the applied window); only Cancel
        // dismisses it.
        <CashflowPeriodEditor
          open
          value={period.value}
          onApply={period.onApply}
          onCancel={() => setIsPeriodEditorOpen(false)}
          onRemove={period.onRemove}
        />
      )}
      {(selected.length > 0 || activeBin) && (
        <div className={styles.chips}>
          {selected.map((key) => {
            const option = options.find((entry) => entry.key === key);
            return (
              <DismissibleTag
                key={key}
                type="gray"
                text={option?.label ?? key}
                dismissTooltipLabel="Remove dimension"
                onClose={() =>
                  onGroupByChange(selected.filter((entry) => entry !== key))
                }
              />
            );
          })}
          {activeBin && (
            <DismissibleTag
              type="teal"
              text={`${BIN_CATEGORY_LABELS[activeBin.category]} Bin: ${
                activeBinLabel ?? ''
              }`}
              dismissTooltipLabel="Remove bin"
              onClose={() => onBinChange(null)}
            />
          )}
          {period.value && (
            <DismissibleTag
              type="teal"
              text={`Period: ${periodChipLabel(period.value)}`}
              dismissTooltipLabel="Remove cashflow period"
              onClose={period.onRemove}
            />
          )}
          {showPercent && (
            <DismissibleTag
              type="teal"
              text="% of Package"
              dismissTooltipLabel="Hide % of Package"
              onClose={() => onTogglePercent(false)}
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={styles.tableFrame}>
      <DataTable<RollupRow>
        data={rows}
        columns={columns}
        getRowId={getRowId}
        isLoading={isLoading}
        controlledState={{
          pagination: {
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
          sorting: controlledSorting,
          globalFilter: searchValue,
          // Dimensions start pinned-left and stay pinned (row labels); the user can also
          // pin/unpin any metric. See the `columnPinning` derivation above.
          columnPinning,
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: data.totalCount,
        }}
        pageSizes={MASTER_DATA_PAGE_SIZES}
        onPaginationChange={handlePaginationChange}
        onSortChange={handleSortChange}
        onGlobalSearchChange={handleGlobalSearchChange}
        onColumnPinningChange={setUserPinning}
        searchPlaceholder="Search grouped rows…"
        leftToolbarActions={[
          <Fragment key="group-by">{groupByControl}</Fragment>,
          <Fragment key="bin-by">{binByControl}</Fragment>,
        ]}
        rightToolbarActions={[<Fragment key="gear">{gear}</Fragment>]}
        betweenToolbarAndContent={betweenToolbarAndContent}
        // Single-column server sort + global search on (controlled from atoms).
        config={{
          globalSearch: true,
          sorting: true,
          multiSort: false,
          columnFilters: false,
          columnReordering: true,
          columnVisibility: false,
        }}
      />
    </div>
  );
}
